import { getVerdictDotClassName } from "../../utils";

import SearchBar from "@/components/ui/SearchBar";
import { cn } from "@/lib/utils";

export interface ScorecardRowFilterOption<TFilter extends string> {
  id: TFilter;
  label: string;
  verdict?: string;
}

interface ScorecardRowFilterBarProps<TFilter extends string> {
  search: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;
  filter: TFilter;
  onFilterChange: (value: TFilter) => void;
  options: ScorecardRowFilterOption<TFilter>[];
  counts: Record<TFilter, number>;
}

/**
 * Shared search + verdict filter toolbar for row-browsing views. Used by both
 * the all-evaluators row list and the per-evaluator row list so the two
 * feel like one consistent surface instead of two different UIs.
 */
function ScorecardRowFilterBar<TFilter extends string>({
  search,
  onSearchChange,
  searchPlaceholder = "Search rows",
  filter,
  onFilterChange,
  options,
  counts,
}: ScorecardRowFilterBarProps<TFilter>) {
  return (
    <div className="space-y-2">
      <SearchBar
        value={search}
        onChange={onSearchChange}
        placeholderPhrasesArray={[searchPlaceholder]}
        size="xs"
        className="w-full"
        inputClassName="rounded-lg"
      />

      <div className="flex flex-wrap gap-0.5">
        {options.map(option => {
          const isActive = filter === option.id;
          const count = counts[option.id] ?? 0;
          return (
            <button
              key={option.id}
              type="button"
              onClick={() => onFilterChange(option.id)}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium transition-colors",
                isActive
                  ? "bg-foreground text-background"
                  : "text-muted-foreground hover:bg-muted/60 hover:text-foreground",
              )}
            >
              {option.verdict ? (
                <span
                  className={cn(
                    "h-1.5 w-1.5 shrink-0 rounded-full",
                    getVerdictDotClassName(option.verdict),
                  )}
                />
              ) : null}
              {option.label}
              <span
                className={cn(
                  "tabular-nums",
                  isActive ? "text-background/70" : "text-muted-foreground/70",
                )}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default ScorecardRowFilterBar;
